import React, { useRef, useState, useEffect } from "react";
import {
  IconX,
  IconTrash,
  IconPackage,
  IconDotsVertical,
  IconScan,
  IconRefresh,
  IconLoader,
  IconPlus,
  IconSearch,
} from "@tabler/icons-react";
import {
  use3PLListData,
  useShippingPartnerManifestCreateMutation,
  useFetchShippingPartnerManifestData,
} from "../../apis/3plApis";
import { useFetchOrderOperations } from "../../apis/operationApis";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import useDebounce from "../../utilities/useDebounce";
import Pagination from "../../components/common/Pagination";
import EntriesSelector from "../../components/common/EntriesSelector";
import { formatDateNewFormat } from "../../utilities/helper";
import NoDataFound from "../../components/common/NoDataFound";
import TableSkeleton from "../../components/skeleton/TableSkeleton";
import { usePermission } from "../../hooks/usePermissionTabs";
import { useLanguage } from "../../context/LanguageContext";

type Props = {
  setHeaderActions?: (node: React.ReactNode) => void;
  setHeaderButtons?: (node: React.ReactNode) => void;
};

export default function Dispatched({
  setHeaderActions,
  setHeaderButtons,
}: Props = {}) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const { allowed: canCreateManifest } = usePermission(
    "pickup_manifests",
    "create",
  );

  const [awbInput, setAwbInput] = useState("");
  const [scannedAwbs, setScannedAwbs] = useState<string[]>([]);
  const [selected3pl, setSelected3pl] = useState("");

  // New States for Scan/Load workflow
  const [actionMode, setActionMode] = useState<"idle" | "scan" | "load">(
    "idle",
  );
  const [selectedLoadedAwbs, setSelectedLoadedAwbs] = useState<string[]>([]);
  const [lastLoaded3pl, setLastLoaded3pl] = useState("");

  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchFor, setSearchFor] = useState("");
  const debouncedSearch = useDebounce(searchFor, 550);
  const [showSearch, setShowSearch] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);

  const [openDropdownIndex, setOpenDropdownIndex] = useState<any>(null);
  const { data: channelsData } = use3PLListData();
  const manifestMutation = useShippingPartnerManifestCreateMutation();

  const {
    data: manifestListData,
    isPending: listPending,
    isFetching,
    refetch,
  } = useFetchShippingPartnerManifestData(pageNo, limit, debouncedSearch);

  // Find selected channel company name
  const selectedChannelObj = channelsData?.find(
    (c: any) => String(c.id) === String(selected3pl),
  );
  const selectedCompanyName = selectedChannelObj
    ? selectedChannelObj.companyName ||
      selectedChannelObj.company_name ||
      selectedChannelObj.title
    : "";

  // Fetch packed shipments for selected 3PL in Load Mode
  const {
    data: packedOrdersResponse,
    isPending: packedPending,
    isFetching: packedFetching,
    refetch: refetchPacked,
  } = useFetchOrderOperations(
    "PACKED",
    1,
    1000,
    "",
    selectedCompanyName ? [{ shippingCompany: selectedCompanyName }] : [],
    { enabled: actionMode === "load" && !!selected3pl },
  );

  const packedOrders = packedOrdersResponse?.data || [];

  // Auto-select all packed shipments when loaded
  useEffect(() => {
    if (
      actionMode === "load" &&
      packedOrders.length > 0 &&
      selected3pl !== lastLoaded3pl
    ) {
      const allAwbs = packedOrders.map((o: any) => o.awbNumber).filter(Boolean);
      setSelectedLoadedAwbs(allAwbs);
      setLastLoaded3pl(selected3pl);
    }
  }, [packedOrders, actionMode, selected3pl, lastLoaded3pl]);

  useEffect(() => {
    if (setHeaderActions) setHeaderActions(null);
  }, [setHeaderActions]);

  useEffect(() => {
    if (!setHeaderButtons) return;

    if (actionMode === "scan") {
      setHeaderButtons(
        <div className="flex items-center gap-2 bg-white/70 backdrop-blur-sm px-4 py-1.5 rounded-xl border border-slate-200 shadow-sm text-[11px] select-none font-bold min-h-8">
          <div className="flex items-center gap-1.5">
            <span className="text-slate-500">Scanned:</span>
            <span className="text-blue-700 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded">
              {scannedAwbs.length} AWB(s)
            </span>
          </div>
        </div>,
      );
    } else if (actionMode === "load") {
      setHeaderButtons(
        <div className="flex items-center gap-2 bg-white/70 backdrop-blur-sm px-4 py-1.5 rounded-xl border border-slate-200 shadow-sm text-[11px] select-none font-bold min-h-8">
          <div className="flex items-center gap-1.5">
            <span className="text-slate-500">Selected:</span>
            <span className="text-blue-700 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded">
              {selectedLoadedAwbs.length}
            </span>
          </div>
          <span className="h-3 w-px bg-slate-300" />
          <div className="flex items-center gap-1.5">
            <span className="text-slate-500">Total:</span>
            <span className="text-slate-700 bg-slate-50 border border-slate-200 px-1.5 py-0.5 rounded">
              {packedOrders.length}
            </span>
          </div>
        </div>,
      );
    } else {
      setHeaderButtons(null);
    }

    return () => setHeaderButtons(null);
  }, [
    setHeaderButtons,
    actionMode,
    scannedAwbs.length,
    selectedLoadedAwbs.length,
    packedOrders.length,
  ]);

  const handle3PLChange = (val: string) => {
    setSelected3pl(val);
    setActionMode("idle");
    setScannedAwbs([]);
    setSelectedLoadedAwbs([]);
    setAwbInput("");
    setLastLoaded3pl("");
  };

  const commitAwb = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    if (scannedAwbs.includes(trimmed)) {
      toast.error(`AWB already added: ${trimmed}`);
      setAwbInput("");
      return;
    }
    setScannedAwbs((prev) => [trimmed, ...prev]);
    setAwbInput("");
  };

  const handleRemoveAwb = (awb: string) => {
    setScannedAwbs((prev) => prev.filter((a) => a !== awb));
  };

  const handleClearAll = () => {
    setScannedAwbs([]);
    toast.success("Cleared all AWBs");
  };

  const handleSubmitManifest = (shipmentsToManifest: string[]) => {
    if (shipmentsToManifest.length === 0) {
      toast.error("Please select or scan at least one shipment");
      return;
    }
    if (!selected3pl) {
      toast.error("Please select a 3PL company");
      return;
    }

    manifestMutation.mutate(
      { shippingPartnerId: selected3pl, shipments: shipmentsToManifest },
      {
        onSuccess: (res: any) => {
          const manifest = res?.data?.manifest;
          const summary = res?.data?.summary;
          const shortId = manifest?.shippingPartnerManifestShortId;

          let message = `Manifest ${shortId || ""} generated successfully!`;
          if (summary?.invalidCount > 0) {
            message += ` ${summary.validCount} shipments added, ${summary.invalidCount} skipped.`;
          }
          toast.success(message, { duration: 5000 });

          if (summary?.invalidCount > 0) {
            summary.invalidOrders?.forEach((item: any) => {
              toast.error(`Skipped ${item.awb}: ${item.error}`, {
                duration: 6000,
              });
            });
          }

          setScannedAwbs([]);
          setSelectedLoadedAwbs([]);
          setAwbInput("");
          setActionMode("idle");
          setShowSidebar(false);
          refetch();
          if (actionMode === "load") {
            refetchPacked();
          }
        },
        onError: (err: any) => {
          toast.error(err?.message || "Failed to generate manifest");
        },
      },
    );
  };

  const getStatusStyle = (status: string) => {
    switch (status) {
      case "Assigned":
        return "bg-[#C6DFF7] border border-[#0088FF] text-[#212121]";
      case "RIW":
        return "bg-[#CCEAD7] border border-[#20BF55] text-[#212121]";
      case "GENERATED":
        return "bg-yellow-100 border border-yellow-400 text-yellow-800";
      default:
        return "bg-[#EEEEEE] border border-[#d2d2d2] text-[#212121]";
    }
  };

  const manifests: any[] = manifestListData?.shippingManifest || [];

  return (
    <div className="grid grid-cols-12 gap-6 p-2  bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl transition-all duration-300 w-ful rounded-r-3xl rounded-bl-3xl w-full">
      <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
      {showSidebar && (
        <div
          className="fixed inset-0 bg-black/40 z-[34] xl:hidden"
          onClick={() => setShowSidebar(false)}
        />
      )}

      {/* Main Content Area */}
      <div
        className={showSidebar ? "col-span-12 xl:col-span-8" : "col-span-12"}
      >
        {/* Unified Main Card matching AllOrders table layout */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-visible mb-6">
          <div className="px-6 py-3.5">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              {/* Left side: Brand badge */}
              <div className="flex flex-wrap items-center gap-3"></div>

              {/* Right side: Entries selector and Sliding Search + Add Button */}
              <div className="flex items-center flex-wrap gap-2.5">
                {manifestListData &&
                  manifestListData.totalPages * limit > 10 && (
                    <EntriesSelector
                      value={limit}
                      options={[
                        10,
                        ...(manifestListData.totalPages * limit > 10
                          ? [20]
                          : []),
                        ...(manifestListData.totalPages * limit > 20
                          ? [30]
                          : []),
                        ...(manifestListData.totalPages * limit > 50
                          ? [50]
                          : []),
                      ]}
                      onChange={(val) => {
                        setLimit(val);
                        setPageNo(1);
                      }}
                    />
                  )}

                {/* Sliding Search */}
                <div className="relative flex items-center group">
                  <div
                    onClick={() => {
                      if (!showSearch) {
                        setShowSearch(true);
                      }
                    }}
                    className={`flex items-center transition-all duration-300 ease-in-out border border-slate-200 rounded-lg bg-white shadow-sm h-8 ${
                      showSearch
                        ? "w-64 px-2 cursor-default"
                        : "w-8 justify-center cursor-pointer hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95"
                    }`}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSearch(!showSearch);
                      }}
                      className="flex items-center justify-center text-slate-700 focus:outline-none flex-shrink-0"
                    >
                      <IconSearch size={17} stroke={2.5} />
                    </button>
                    {showSearch && (
                      <>
                        <input
                          type="text"
                          value={searchFor}
                          onChange={(e) => {
                            setSearchFor(e.target.value);
                            setPageNo(1);
                          }}
                          placeholder={t("Search...")}
                          autoFocus
                          className="flex-1 ml-2 bg-transparent text-xs text-slate-800 placeholder-slate-400 outline-none border-none"
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSearchFor("");
                            setShowSearch(false);
                            setPageNo(1);
                          }}
                          className="flex items-center justify-center p-0.5 ml-1 text-slate-400 hover:text-slate-600 focus:outline-none flex-shrink-0"
                          title={t("Clear & Close")}
                        >
                          <IconX size={14} stroke={2.5} />
                        </button>
                      </>
                    )}
                  </div>
                  {!showSearch && (
                    <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50">
                      {t("Search")}
                    </div>
                  )}
                </div>

                {/* Add Manifest (Toggles Sidebar Form) */}
                {canCreateManifest && (
                  <div className="relative group">
                    <button
                      onClick={() => setShowSidebar(!showSidebar)}
                      className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-150 border shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer ${
                        showSidebar
                          ? "bg-[#FBD037] border-[#FBD037] text-[#131327] hover:bg-[#e2bd30]"
                          : "bg-white hover:bg-slate-50 border-slate-200 text-slate-700"
                      }`}
                    >
                      <IconPlus size={17} stroke={2.5} />
                    </button>
                    <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50">
                      {t("Create Manifest")}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Table/Loader container inside the Unified Card */}
          <div className="border-t border-slate-100">
            {listPending || isFetching ? (
              <div className="p-4 bg-[#FCFDF8] rounded-b-3xl">
                <TableSkeleton
                  columns={
                    showSidebar
                      ? [
                          "#",
                          t("Manifest ID"),
                          t("Shipments"),
                          t("Company Name"),
                          t("Status"),
                          t("Actions"),
                        ]
                      : [
                          "#",
                          t("Manifest ID"),
                          t("Shipments"),
                          t("Company Name"),
                          t("Date"),
                          t("Driver"),
                          t("Status"),
                          t("Actions"),
                        ]
                  }
                  rows={5}
                />
              </div>
            ) : manifests.length > 0 ? (
              <div className="bg-[#F7F6F2] rounded-b-3xl">
                <div className="overflow-y-auto max-h-[75vh] hide-scrollbar bg-[#FCFDF8] rounded-b-3xl shadow-inner">
                  <table className="w-full text-left border-collapse table-fixed">
                    <colgroup>
                      <col style={{ width: "5%" }} />
                      <col style={{ width: "15%" }} />
                      <col style={{ width: "10%" }} />
                      <col style={{ width: showSidebar ? "45%" : "25%" }} />
                      {!showSidebar && <col style={{ width: "15%" }} />}
                      {!showSidebar && <col style={{ width: "15%" }} />}
                      <col style={{ width: showSidebar ? "15%" : "10%" }} />
                      <col style={{ width: "10%" }} />
                    </colgroup>
                    <thead className="text-[#131327]/70 border-b border-dashed border-gray-300 sticky top-0 z-20 bg-[#FCFDF8]">
                      <tr className="text-center text-[11px] font-semibold tracking-wider uppercase select-none">
                        <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200 text-center">
                          #
                        </th>
                        <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200">
                          {t("Manifest ID")}
                        </th>
                        <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200">
                          {t("Shipments")}
                        </th>
                        <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200">
                          {t("Company Name")}
                        </th>
                        {!showSidebar && (
                          <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200">
                            {t("Date")}
                          </th>
                        )}
                        {!showSidebar && (
                          <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200">
                            {t("Driver")}
                          </th>
                        )}
                        <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200 text-center">
                          {t("Status")}
                        </th>
                        <th className="px-2 pt-3 pb-2 text-center">{t("Actions")}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {manifests.map((m: any, index: number) => {
                        const sn = (pageNo - 1) * limit + index + 1;
                        const getStatusBadgeStyle = (status: string) => {
                          const normStatus = (status || "").toUpperCase();
                          switch (normStatus) {
                            case "ASSIGNED":
                              return "bg-blue-50 border-blue-200 text-blue-700 border";
                            case "RIW":
                              return "bg-emerald-50 border-emerald-200 text-emerald-700 border";
                            case "GENERATED":
                              return "bg-amber-50 border-amber-200 text-amber-700 border";
                            default:
                              return "bg-slate-50 border-slate-200 text-slate-600 border";
                          }
                        };
                        return (
                          <tr
                            key={m.id}
                            className="hover:bg-[#FBD037]/10 group transition-colors duration-150"
                          >
                            <td className="px-2 py-3 text-xs font-semibold text-[#212121] text-center">
                              {sn}
                            </td>
                            <td className="px-2 py-3 min-w-0 text-center">
                              <div
                                className="text-xs font-bold text-[#212121]"
                                title={m.shortId || m.id}
                              >
                                {m.shortId || m.id?.slice(0, 8)}
                              </div>
                            </td>
                            <td className="px-2 py-3 min-w-0 text-center">
                              <span className="inline-flex items-center justify-center bg-slate-900 text-white rounded-full w-7 h-7 font-black text-xs select-none">
                                {m.shipmentCount}
                              </span>
                            </td>
                            <td className="px-2 py-3 min-w-0 text-center">
                              <span
                                className="text-xs font-black text-[#856608] hover:underline cursor-pointer block truncate"
                                title={m.companyName}
                              >
                                {m.companyName}
                              </span>
                            </td>
                            {!showSidebar && (
                              <td className="px-2 py-3 min-w-0 text-center">
                                <div className="text-xs font-bold text-[#212121]">
                                  {formatDateNewFormat(m.createdAt)}
                                </div>
                              </td>
                            )}
                            {!showSidebar && (
                              <td className="px-2 py-3 min-w-0 text-center">
                                <div
                                  className="text-xs font-medium text-[#212121] truncate"
                                  title={m.driverName || "—"}
                                >
                                  {m.driverName || "—"}
                                </div>
                              </td>
                            )}
                            <td className="px-2 py-3 text-center">
                              <span
                                className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold tracking-wide inline-block truncate max-w-full align-middle ${getStatusBadgeStyle(
                                  m.status,
                                )}`}
                                title={m.status || "—"}
                              >
                                {t(m.status) || "—"}
                              </span>
                            </td>
                            <td className="px-2 py-3 text-center">
                              <div className="relative inline-block">
                                <button
                                  onClick={() =>
                                    setOpenDropdownIndex(
                                      openDropdownIndex === m.id ? null : m.id,
                                    )
                                  }
                                  className="p-1.5 border border-[#131327]/10 rounded-full bg-[#F7F6F2] hover:bg-[#e8e7e3] text-gray-600 hover:text-[#212121] transition-colors"
                                  title={t("Actions")}
                                >
                                  <IconDotsVertical size={13} stroke={2.5} />
                                </button>
                                {openDropdownIndex === m.id && (
                                  <div className="absolute right-8 -top-1 z-50 mt-2 text-left rounded-xl shadow-lg bg-[#F7F6F2] divide-y divide-[#979797]/30 overflow-hidden min-w-[130px]">
                                    <div
                                      onClick={() =>
                                        navigate(
                                          `/shipping-menifest-detail/${m.id}`,
                                        )
                                      }
                                      className="px-4 py-2 text-[#404040] hover:bg-[#FBD037] cursor-pointer text-sm font-semibold"
                                    >
                                      {t("View Details")}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="py-16 text-center bg-white rounded-b-3xl">
                <NoDataFound />
              </div>
            )}
          </div>
        </div>

        {manifestListData?.totalPages > 1 && (
          <div className="flex items-center justify-between px-2 pb-4">
            <p className="text-xs text-slate-500 font-semibold">
              Page <span className="text-slate-800 font-black">{pageNo}</span>{" "}
              of{" "}
              <span className="text-slate-800 font-black">
                {manifestListData.totalPages}
              </span>
            </p>
            <div className="flex items-center gap-1">
              <Pagination
                totalPages={manifestListData.totalPages}
                currentPage={pageNo}
                fetchData={setPageNo}
              />
            </div>
          </div>
        )}
      </div>

      {/* Right Sidebar Area */}
      {showSidebar && (
        <div className="col-span-12 xl:col-span-4 space-y-4 bg-[#F7F6F2] xl:static fixed inset-y-0 right-0 z-[35] w-full sm:w-[450px] xl:w-auto p-4 pt-[76px] sm:pt-[96px] xl:p-0 shadow-2xl xl:shadow-none bg-white xl:bg-transparent overflow-y-auto xl:overflow-visible flex flex-col justify-start">
          <div className="bg-white border border-[#E0E0E0] rounded-2xl shadow-sm p-5 flex flex-col gap-5 transition-all duration-300 relative">
            {/* Close Button */}
            <button
              onClick={() => setShowSidebar(false)}
              className="absolute top-4 right-4 p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors z-10"
              title={t("Close Sidebar")}
            >
              <IconX size={18} />
            </button>

            {/* Sidebar content (top form) */}
            {/* Row 1: 3PL Selection */}
            <div className="flex flex-col gap-2 border-b border-gray-100 pb-4 pr-6">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {t("Select Shipping Carrier (3PL)")}
              </label>
              <div className="relative w-full">
                <select
                  value={selected3pl}
                  onChange={(e) => handle3PLChange(e.target.value)}
                  className="w-full border border-gray-300 hover:border-gray-400 px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-amber-400 text-sm bg-white appearance-none cursor-pointer font-medium text-[#212121] shadow-sm transition-colors"
                >
                  <option value="">{t("Select 3PL Company...")}</option>
                  {channelsData?.map((channel: any) => (
                    <option key={channel.id} value={channel.id}>
                      {channel.companyName ||
                        channel.company_name ||
                        channel.title}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
                  <svg
                    className="fill-current h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
              <p className="text-[10px] text-gray-400">
                {t("Choose a 3PL company to start manifesting shipments")}
              </p>
            </div>

            {/* Row 2: Scan / Load Action Panels */}
            {!selected3pl ? (
              <div className="py-6 flex flex-col items-center justify-center text-center bg-gray-50/50 rounded-xl border border-dashed border-gray-200">
                <IconPackage
                  size={36}
                  className="text-gray-300 mb-2 animate-pulse"
                />
                <p className="text-sm font-medium text-gray-500">
                  {t("Select a 3PL company above to unlock Scan & Load options")}
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {/* Action Mode Toggle Buttons */}
                <div className="flex items-center gap-3 bg-gray-50 p-1.5 rounded-xl border border-gray-200/60 w-fit">
                  <button
                    type="button"
                    onClick={() => {
                      setActionMode("scan");
                      setScannedAwbs([]);
                      setSelectedLoadedAwbs([]);
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold tracking-wide uppercase transition-all duration-200 ${
                      actionMode === "scan"
                        ? "bg-[#FBD037] text-black shadow-sm"
                        : "text-gray-600 hover:text-black hover:bg-gray-100"
                    }`}
                  >
                    <IconScan size={16} />
                    {t("Scan & Paste")}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setActionMode("load");
                      setScannedAwbs([]);
                      setSelectedLoadedAwbs([]);
                      setLastLoaded3pl("");
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold tracking-wide uppercase transition-all duration-200 ${
                      actionMode === "load"
                        ? "bg-[#FBD037] text-black shadow-sm"
                        : "text-gray-600 hover:text-black hover:bg-gray-100"
                    }`}
                  >
                    <IconRefresh size={16} />
                    {t("Load Packed")}
                  </button>
                </div>

                {/* Mode Views */}
                {actionMode === "idle" && (
                  <div className="py-8 flex flex-col items-center justify-center text-center bg-amber-50/30 rounded-xl border border-dashed border-amber-200/80">
                    <p className="text-xs font-medium text-amber-800">
                      {t("Please select either")} <strong>{t("Scan & Paste")}</strong> {t("or")}{" "}
                      <strong>{t("Load Packed")}</strong> {t("mode to continue.")}
                    </p>
                  </div>
                )}

                {/* SCAN MODE VIEW */}
                {actionMode === "scan" && (
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-col gap-3">
                      <div
                        className="w-full border border-gray-300 rounded-xl px-4 py-3 flex flex-wrap gap-2 min-h-[48px] max-h-60 overflow-y-auto cursor-text bg-white focus-within:ring-2 focus-within:ring-amber-400 focus-within:border-amber-400 transition-all shadow-inner"
                        onClick={() => inputRef.current?.focus()}
                      >
                        {scannedAwbs.map((awb) => (
                          <span
                            key={awb}
                            className="flex items-center bg-amber-50 border border-amber-300 text-amber-900 text-xs font-mono px-3 py-1.5 rounded-lg shadow-sm"
                          >
                            <span className="truncate max-w-[120px]">
                              {awb}
                            </span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveAwb(awb);
                              }}
                              className="text-amber-500 hover:text-red-600 transition-colors ml-2 flex-shrink-0"
                            >
                              <IconX size={12} />
                            </button>
                          </span>
                        ))}

                        <input
                          ref={inputRef}
                          type="text"
                          value={awbInput}
                          placeholder={
                            scannedAwbs.length === 0
                              ? t("Scan or paste AWBs here...")
                              : t("Scan next AWB...")
                          }
                          className="outline-none text-sm w-full bg-transparent placeholder:text-gray-400 py-1 flex-1 min-w-[100px]"
                          onChange={(e) => {
                            const val = e.target.value;
                            if (
                              val.endsWith(" ") ||
                              val.endsWith(",") ||
                              val.endsWith("\n")
                            ) {
                              const parts = val
                                .split(/[\s,\n]+/)
                                .map((p) => p.trim())
                                .filter(Boolean);
                              parts.forEach((part) => {
                                if (scannedAwbs.includes(part)) {
                                    toast.error(`AWB already added: ${part}`);
                                } else {
                                  setScannedAwbs((prev) => [part, ...prev]);
                                }
                              });
                              setAwbInput("");
                            } else {
                              setAwbInput(val);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              commitAwb(awbInput);
                            }
                            if (
                              e.key === "Backspace" &&
                              awbInput === "" &&
                              scannedAwbs.length > 0
                            ) {
                              handleRemoveAwb(scannedAwbs[0]);
                            }
                          }}
                          onPaste={(e) => {
                            e.preventDefault();
                            const pasted = e.clipboardData.getData("text");
                            const parts = pasted
                              .split(/[\s,\n]+/)
                              .map((p) => p.trim())
                              .filter(Boolean);
                            const added: string[] = [];
                            parts.forEach((part) => {
                              if (
                                scannedAwbs.includes(part) ||
                                added.includes(part)
                              ) {
                                toast.error(`AWB already added: ${part}`);
                              } else {
                                added.push(part);
                              }
                            });
                            if (added.length > 0) {
                              setScannedAwbs((prev) => [...added, ...prev]);
                              toast.success(`${added.length} AWB(s) added`);
                            }
                            setAwbInput("");
                          }}
                        />
                      </div>

                      <div className="flex items-center justify-end gap-2 w-full">
                        {scannedAwbs.length > 0 && (
                          <button
                            type="button"
                            onClick={handleClearAll}
                            title={t("Clear all AWBs")}
                            className="p-2.5 bg-red-50 text-red-500 hover:bg-red-100 rounded-xl transition-colors border border-red-200"
                          >
                            <IconTrash size={18} />
                          </button>
                        )}

                        <button
                          type="button"
                          onClick={() => {
                            const all = [...scannedAwbs];
                            const leftover = awbInput.trim();
                            if (leftover && !all.includes(leftover))
                              all.unshift(leftover);
                            handleSubmitManifest(all);
                          }}
                          disabled={
                            manifestMutation.isPending ||
                            (scannedAwbs.length === 0 && !awbInput.trim())
                          }
                          className="bg-[#FBD037] hover:bg-[#ebd037]/90 text-black px-4 py-2.5 rounded-xl shadow font-semibold disabled:opacity-50 disabled:cursor-not-allowed text-xs flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-[0.98] transition-all flex-1"
                        >
                          {manifestMutation.isPending ? (
                            <>
                              <IconLoader size={16} className="animate-spin" />
                              {t("Generating...")}
                            </>
                          ) : (
                            t("Generate Manifest")
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* LOAD MODE VIEW */}
                {actionMode === "load" && (
                  <div className="flex flex-col gap-4 bg-gray-50/50 p-4 rounded-xl border border-gray-200/80">
                    {packedPending || packedFetching ? (
                      <div className="flex flex-col items-center justify-center py-8 gap-2">
                        <IconLoader
                          size={28}
                          className="text-amber-500 animate-spin"
                        />
                        <p className="text-xs text-gray-500 font-medium">
                          {t("Fetching packed shipments...")}
                        </p>
                      </div>
                    ) : packedOrders.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-8 text-center gap-2">
                        <IconPackage size={32} className="text-gray-400" />
                        <p className="text-sm font-medium text-gray-600">
                          {t("No packed shipments found")}
                        </p>
                        <p className="text-xs text-gray-400">
                          {t("All shipments for")} {selectedCompanyName} {t("have been dispatched or are pending.")}
                        </p>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-4">
                        <div className="flex items-center justify-between">
                          <div className="text-xs font-semibold text-gray-500">
                            {t("Found")} {packedOrders.length} {t("packed shipment(s)")}
                          </div>
                          <div className="flex items-center gap-3">
                            <button
                              type="button"
                              onClick={() => {
                                const allAwbs = packedOrders
                                  .map((o: any) => o.awbNumber)
                                  .filter(Boolean);
                                if (
                                  selectedLoadedAwbs.length === allAwbs.length
                                ) {
                                  setSelectedLoadedAwbs([]);
                                } else {
                                  setSelectedLoadedAwbs(allAwbs);
                                }
                              }}
                              className="text-xs text-amber-600 hover:text-amber-700 font-semibold transition-colors"
                            >                              {selectedLoadedAwbs.length === packedOrders.length ? t("Deselect All") : t("Select All")}
</button>
                          </div>
                        </div>

                        {/* Scrollable mini table */}
                        <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg bg-white shadow-inner">
                          <table className="w-full text-xs text-left">
                            <thead className="bg-gray-100 text-gray-600 sticky top-0 border-b border-gray-200 text-[10px]">
                              <tr>
                                <th className="px-2 py-2 w-8 text-center">
                                  <input
                                    type="checkbox"
                                    checked={
                                      selectedLoadedAwbs.length ===
                                        packedOrders.length &&
                                      packedOrders.length > 0
                                    }
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        const allAwbs = packedOrders
                                          .map((o: any) => o.awbNumber)
                                          .filter(Boolean);
                                        setSelectedLoadedAwbs(allAwbs);
                                      } else {
                                        setSelectedLoadedAwbs([]);
                                      }
                                    }}
                                    className="rounded text-amber-500 focus:ring-amber-500 cursor-pointer h-3.5 w-3.5"
                                  />
                                </th>
                                <th className="px-2 py-2 font-medium">
                                  {t("AWB Number")}
                                </th>
                                <th className="px-2 py-2 font-medium">
                                  {t("Client Name")}
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {packedOrders.map((order: any) => {
                                const isChecked = selectedLoadedAwbs.includes(
                                  order.awbNumber,
                                );
                                return (
                                  <tr
                                    key={order.id}
                                    className={`hover:bg-amber-50/40 transition-colors cursor-pointer ${isChecked ? "bg-amber-50/20" : ""}`}
                                    onClick={() => {
                                      if (isChecked) {
                                        setSelectedLoadedAwbs((prev) =>
                                          prev.filter(
                                            (a) => a !== order.awbNumber,
                                          ),
                                        );
                                      } else {
                                        setSelectedLoadedAwbs((prev) => [
                                          ...prev,
                                          order.awbNumber,
                                        ]);
                                      }
                                    }}
                                  >
                                    <td
                                      className="px-2 py-2 text-center"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setSelectedLoadedAwbs((prev) => [
                                              ...prev,
                                              order.awbNumber,
                                            ]);
                                          } else {
                                            setSelectedLoadedAwbs((prev) =>
                                              prev.filter(
                                                (a) => a !== order.awbNumber,
                                              ),
                                            );
                                          }
                                        }}
                                        className="rounded text-amber-500 focus:ring-amber-500 cursor-pointer h-3.5 w-3.5"
                                      />
                                    </td>
                                    <td
                                      className="px-2 py-2 font-mono font-medium text-gray-900 truncate max-w-[120px]"
                                      title={order.awbNumber}
                                    >
                                      {order.awbNumber}
                                    </td>
                                    <td
                                      className="px-2 py-2 text-gray-600 truncate max-w-[100px]"
                                      title={order.clientName}
                                    >
                                      {order.clientName || "—"}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        <div className="flex justify-end items-center gap-3">
                          <button
                            type="button"
                            onClick={() =>
                              handleSubmitManifest(selectedLoadedAwbs)
                            }
                            disabled={
                              manifestMutation.isPending ||
                              selectedLoadedAwbs.length === 0
                            }
                            className="bg-[#FBD037] hover:bg-[#ebd037]/90 text-black px-4 py-2 rounded-xl shadow font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 hover:scale-[1.02] active:scale-[0.98] transition-all text-xs w-full justify-center"
                          >
                            {manifestMutation.isPending ? (
                              <>
                                <IconLoader
                                  size={16}
                                  className="animate-spin"
                                />
                                {t("Generating...")}
                              </>
                            ) : (
                              `${t("Generate")} (${selectedLoadedAwbs.length})`
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
